import { useMemo, useCallback } from 'react';
import { useCustomColumns } from './useCustomColumns';
import { useLeadsTableConfig } from './useLeadsTableConfig';
import { ColumnConfigItem, CustomColumnDefinition } from '../../types/lead';

export const DEFAULT_LEAD_COLUMNS: ColumnConfigItem[] = [
  { key: 'lead', label: 'Lead', is_default: true, order: 0, visible: true },
  { key: 'lead_status', label: 'Status', is_default: true, order: 1, visible: true },
  { key: 'source', label: 'Source', is_default: true, order: 2, visible: true },
  { key: 'created_at', label: 'Created', is_default: true, order: 3, visible: true },
  { key: 'notes', label: 'Notes', is_default: true, order: 4, visible: true },
  { key: 'actions', label: 'Actions', is_default: true, order: 5, visible: true },
];

export function resolveLeadColumns(
  customColumns: CustomColumnDefinition[],
  columnsConfig?: ColumnConfigItem[]
): ColumnConfigItem[] {
  // Step 1: Filter active custom columns
  const activeCustomCols = customColumns.filter((col) => col.is_active !== false);

  const availableMap = new Map<string, ColumnConfigItem>();

  // Populate static immutable default columns
  DEFAULT_LEAD_COLUMNS.forEach((col) => {
    availableMap.set(col.key, { ...col });
  });

  // Populate active custom columns (default visible: true)
  activeCustomCols.forEach((col, idx) => {
    availableMap.set(col.key, {
      key: col.key,
      label: col.name,
      is_default: false,
      order: DEFAULT_LEAD_COLUMNS.length + idx,
      visible: true,
      type: col.type,
      options: col.options,
    });
  });

  // Step 2: Merge with table configuration if present
  if (columnsConfig && columnsConfig.length > 0) {
    const resolved: ColumnConfigItem[] = [];
    const processedKeys = new Set<string>();

    const sortedConfig = [...columnsConfig].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    for (const configItem of sortedConfig) {
      const baseCol = availableMap.get(configItem.key);
      if (baseCol) {
        resolved.push({
          ...baseCol,
          // Always use baseCol.label so default and renamed custom columns show their latest name
          label: baseCol.label,
          order: configItem.order,
          visible: configItem.visible ?? true,
        });
        processedKeys.add(configItem.key);
      }
    }

    // Step 3: Append any new custom columns not yet in columnsConfig
    let nextOrder = resolved.length;
    availableMap.forEach((col, key) => {
      if (!processedKeys.has(key)) {
        resolved.push({
          ...col,
          order: nextOrder++,
          visible: true, // Default visible for new custom columns
        });
      }
    });

    return resolved;
  }

  // Fallback if no table config exists: Default columns visible, active custom columns hidden by default
  return Array.from(availableMap.values());
}

export function useLeadColumns(smbId?: string) {
  const {
    customColumns,
    loading: customLoading,
    error: customError,
    fetchCustomColumns,
    createCustomColumn,
    renameCustomColumn,
    deactivateCustomColumn,
    addOptionToColumn,
    renameOptionInColumn,
    updateColumnOptions,
    patchCustomColumn,
  } = useCustomColumns(smbId);

  const {
    columnsConfig,
    loading: configLoading,
    error: configError,
    fetchTableConfig,
    saveTableConfig,
    setColumnsConfig,
  } = useLeadsTableConfig(smbId);

  const resolvedColumns = useMemo(() => {
    return resolveLeadColumns(customColumns, columnsConfig);
  }, [customColumns, columnsConfig]);

  const visibleColumns = useMemo(() => {
    return resolvedColumns.filter((col) => col.visible !== false);
  }, [resolvedColumns]);

  const hideColumn = useCallback(
    async (columnKey: string) => {
      const updated = resolvedColumns.map((col) => {
        if (col.key === columnKey) {
          return { ...col, visible: false };
        }
        return col;
      });
      return await saveTableConfig(updated);
    },
    [resolvedColumns, saveTableConfig]
  );

  const refetch = () => {
    fetchCustomColumns();
    fetchTableConfig();
  };

  return {
    customColumns,
    columnsConfig,
    resolvedColumns,
    visibleColumns,
    loading: customLoading || configLoading,
    error: customError || configError,
    createCustomColumn,
    renameCustomColumn,
    deactivateCustomColumn,
    addOptionToColumn,
    renameOptionInColumn,
    updateColumnOptions,
    patchCustomColumn,
    saveTableConfig,
    setColumnsConfig,
    hideColumn,
    refetch,
  };
}

